import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Autorenew, Download } from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import { buildPublicFormUrl } from 'src/apis/eezyguard';

type Props = {
  slug: string | null;
  enabled: boolean;
  enabledAt?: string | null;
  canRegenerate?: boolean;
  onRegenerate?: () => void;
  regenerating?: boolean;
};

export default function LinkQrPanel({
  slug,
  enabled,
  enabledAt,
  canRegenerate,
  onRegenerate,
  regenerating,
}: Props): JSX.Element {
  const url = buildPublicFormUrl(slug);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQr = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `eezyline-${slug}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Link público e QR Code
          </Typography>
          <Chip
            size="small"
            label={enabled ? 'Ativo' : 'Inativo'}
            color={enabled ? 'success' : 'default'}
          />
        </Stack>

        {!slug ? (
          <Typography variant="body2" color="text.secondary">
            O link é gerado automaticamente quando o módulo é ativado.
          </Typography>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Box ref={canvasRef} sx={{ bgcolor: '#fff', p: 1, borderRadius: 1 }}>
              <QRCodeCanvas value={url} size={128} includeMargin />
            </Box>
            <Box flex={1} width="100%">
              <TextField
                fullWidth
                size="small"
                value={url}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                      <IconButton onClick={copyLink} edge="end" size="small">
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              {enabledAt && (
                <Typography variant="caption" color="text.secondary">
                  Ativado em {new Date(enabledAt).toLocaleDateString('pt-BR')}
                </Typography>
              )}
              <Stack direction="row" spacing={1} mt={1}>
                <Button size="small" startIcon={<Download />} onClick={downloadQr}>
                  Baixar QR
                </Button>
                {canRegenerate && (
                  <Button
                    size="small"
                    color="warning"
                    startIcon={<Autorenew />}
                    onClick={onRegenerate}
                    disabled={regenerating}
                  >
                    Regenerar link
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
